import React, { useState } from 'react';
import { FiUser, FiX } from 'react-icons/fi';
import { WidgetDef, SettingValues, GENERATE_NOTE } from '../types';
import { DemoHead } from '../shared';
import { toast } from '../../../Toast';

interface Person { name: string; role: string; appointed: string; bio: string }

const GROUPS: { key: string; label: string; people: Person[] }[] = [
    {
        key: 'directors', label: 'Board of Directors', people: [
            { name: 'Margaret Chua', role: 'Non‑Executive Chair', appointed: '2019', bio: 'Thirty years in mining finance, including a decade as CFO of an ASX 200 gold producer.' },
            { name: 'David Nguyen', role: 'Managing Director', appointed: '2019', bio: 'Geologist who led the discovery team at Gold Mountain before taking the company to the ASX.' },
            { name: 'Sarah O’Neill', role: 'Non‑Executive Director', appointed: '2019', bio: 'Corporate lawyer specialising in resources, capital raisings and native title agreements.' },
        ],
    },
    {
        key: 'management', label: 'Management', people: [
            { name: 'James Patel', role: 'Chief Financial Officer', appointed: '2021', bio: 'Chartered accountant with fifteen years in mid‑tier mining and project finance.' },
            { name: 'Emma Walsh', role: 'Exploration Manager', appointed: '2022', bio: 'Runs the drilling programmes across Gold Mountain and Kimberley South.' },
        ],
    },
    {
        key: 'secretary', label: 'Company Secretary', people: [
            { name: 'Rachel Kim', role: 'Company Secretary', appointed: '2020', bio: 'Governance professional and secretary to several ASX‑listed explorers.' },
        ],
    },
];

const initials = (name: string) => name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
const slugOf = (name: string) => name.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '');

const PersonCard: React.FC<{ p: Person; s: SettingValues; onPopup: (p: Person) => void }> = ({ p, s, onPopup }) => {
    const [open, setOpen] = useState(false);
    const readBio = () => {
        if (s.bio === 'In the card') setOpen(!open);
        else if (s.bio === 'Pop-up') onPopup(p);
        else toast.info(`Preview only: opens /about-us/board-management/${slugOf(p.name)}/`);
    };
    const photoClass = `photo ${String(s.photoSize).toLowerCase()} ${String(s.photoShape).toLowerCase()}`;
    return <div className="wd-person">
        {s.photo && <div className={photoClass}><span>{initials(p.name)}</span></div>}
        <div className="body">
            <strong>{p.name}</strong>
            {s.role && <span className="role">{p.role}</span>}
            {s.appointed && <span className="muted-line">Appointed {p.appointed}</span>}
            {open && <p className="bio">{p.bio}</p>}
            <button type="button" className="read" onClick={readBio}>{open ? 'Hide bio ↑' : 'Read full bio →'}</button>
        </div>
    </div>;
};

const BoardDemo: React.FC<{ settings: SettingValues }> = ({ settings: s }) => {
    const groups = GROUPS.filter(g => s[g.key]);
    const [picked, setPicked] = useState(GROUPS[0].key);
    const [popup, setPopup] = useState<Person | null>(null);
    const current = groups.find(g => g.key === picked) ?? groups[0];
    // one widget per group, or no filter: every group on the page under its own heading
    const stacked = s.split === 'One widget per group' || s.groupFilter === 'None';

    const cards = (people: Person[]) => <div className="wd-people">
        {people.map(p => <PersonCard key={p.name} p={p} s={s} onPopup={setPopup} />)}
    </div>;

    let body: React.ReactNode = <p className="wd-media-empty">Switch on at least one group.</p>;
    if (groups.length && stacked) body = groups.map(g => <div key={g.key} className="wd-board-group"><h4>{g.label}</h4>{cards(g.people)}</div>);
    else if (current && s.groupFilter === 'Dropdown') body = <>
        <select className="wd-media-select wd-board-select" aria-label="Group" value={current.key} onChange={e => setPicked(e.target.value)}>
            {groups.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
        </select>
        {cards(current.people)}
    </>;
    else if (current) body = <>
        <div className="wd-media-tabs boxed" role="tablist" aria-label="Group">
            {groups.map(g => <button key={g.key} type="button" role="tab" aria-selected={g.key === current.key}
                className={g.key === current.key ? 'active' : ''} onClick={() => setPicked(g.key)}>{g.label}</button>)}
        </div>
        {cards(current.people)}
    </>;

    return <div className="wd-board">
        <DemoHead title="Board & management" sub="Directors, management and the company secretary" />
        {body}
        {popup && <div className="wd-bio-pop" role="dialog" aria-label={popup.name} onClick={() => setPopup(null)}>
            <div className="card-pop" onClick={e => e.stopPropagation()}>
                <button type="button" className="close" aria-label="Close" onClick={() => setPopup(null)}><FiX /></button>
                <strong>{popup.name}</strong>
                <span className="role">{popup.role}</span>
                <p>{popup.bio}</p>
            </div>
        </div>}
    </div>;
};

// spec 622:5388
const boardManagement: WidgetDef = {
    slug: 'board-management',
    name: 'Board & management',
    description: 'Directors, management and the company secretary',
    icon: <FiUser />,
    title: 'Board & Management Widget',
    installed: false,
    page: null,
    mustInstall: true,
    views: null,
    savedCodes: [
        { path: '/about/board-management/', note: 'All groups · tabs' },
        { path: '/about/', note: 'Directors only' },
    ],
    // the spec highlights neither saved code
    savedCodeSelected: -1,
    demoPath: '/investors/',
    addNote: 'This code is generated from the settings below. Change a setting and the code updates — paste it once, it stays current.',
    addBlocks: [
        { title: '1 · WordPress shortcode', code: '[investi_board instance="board" filter="tabs"]' },
        { title: '2 · Or plain HTML', code: '<div data-investi-instance="board" data-investi="board"\n     data-investi-filter="tabs"></div>', dark: true },
        {
            title: '3 · Or one group at a time',
            codes: [
                { label: 'Board of Directors', code: '[investi_board group="directors"]' },
                { label: 'Management', code: '[investi_board group="management"]' },
                { label: 'Company Secretary', code: '[investi_board group="secretary"]' },
            ],
            after: 'Use these three instead of the code above when each group sits on its own page — each one keeps its own settings.',
        },
    ],
    settings: [
        { kind: 'heading', label: 'How it is split' },
        { kind: 'segmented', key: 'split', options: ['All in one widget', 'One widget per group'], default: 'All in one widget' },
        { kind: 'toggle', key: 'directors', label: 'Board of Directors', default: true },
        { kind: 'toggle', key: 'management', label: 'Management', default: true },
        { kind: 'toggle', key: 'secretary', label: 'Company Secretary', default: true },
        // separate widgets have nothing to switch between
        { kind: 'segmented', key: 'groupFilter', label: 'Group filter', options: ['Tabs', 'Dropdown', 'None'], default: 'Tabs', inline: true, disabled: s => s.split === 'One widget per group' },
        { kind: 'note', text: 'All in one widget gives you a single code with a tab or dropdown to switch groups. One widget per group gives you three separate codes, so each group can sit on its own page — see "Add It to a Page".' },
        { kind: 'heading', label: 'The full biography' },
        { kind: 'segmented', key: 'bio', options: ['In the card', 'Pop-up', 'Post page'], default: 'Pop-up' },
        { kind: 'note', text: 'Post page opens /about-us/board-management/name/ — a separate WordPress post type, kept out of your news feed.' },
        { kind: 'heading', label: 'What each card shows' },
        { kind: 'toggle', key: 'photo', label: 'Photograph', default: true },
        { kind: 'segmented', key: 'photoSize', label: 'Photo size', options: ['Small', 'Medium', 'Large'], default: 'Large', inline: true, disabled: s => !s.photo },
        { kind: 'segmented', key: 'photoShape', label: 'Photo shape', options: ['Square', 'Portrait', 'Circle'], default: 'Square', inline: true, disabled: s => !s.photo },
        { kind: 'custom', render: () => <p className="wd-muted">Large fills the card, the way most mining company sites show their board. Upload at 1000 × 1000 px or bigger so it stays sharp.</p> },
        { kind: 'toggle', key: 'role', label: 'Role and title', default: true },
        { kind: 'toggle', key: 'committees', label: 'Committees they sit on', default: true },
        { kind: 'toggle', key: 'linkedin', label: 'LinkedIn link', default: true },
        { kind: 'toggle', key: 'appointed', label: 'Appointment date', default: true },
    ],
    generateNote: GENERATE_NOTE,
    Demo: BoardDemo,
};

export default boardManagement;
